"use client";

import { useEffect, useState, useTransition } from "react";
import Image from "next/image";
import { useTranslations } from "next-intl";
import { ArrowRight, Sparkles, TrendingUp } from "lucide-react";
import { defaultLocale, formatPrice, isValidLocale, type Locale } from "@/config/locale";
import { fetchPricingResolveItems, pricingItemsById } from "@/lib/pricing/pricing-resolve-client";
import type { ResolvedMoneyPresentation } from "@/lib/pricing/resolve-display-money";
import { cn } from "@/lib/utils";
import { normalizeSaleorMediaUrl } from "@/lib/media-url";
import { UPSELL_BESTSELLERS } from "@/config/brand";
import { SHIPPING_CONFIG } from "@/config/shipping";
import { addCartLine, upgradeCartLine, fetchProductForUpsell, type UpsellVariantData } from "./actions";
import { useGateCartAction } from "@/hooks/use-gate-cart-action";

interface CartUpsellProps {
	lines: Array<{
		id: string;
		variantId: string;
		variantName: string;
		productSlug: string;
		productName: string;
		thumbnailUrl?: string;
		totalPrice: number;
		saleUnit?: string | null;
	}>;
	subtotal: number;
	checkoutId: string;
	channel: string;
	locale: string;
}

interface UpgradeOffer {
	type: "upgrade";
	lineId: string;
	productName: string;
	thumbnailUrl: string;
	currentPack: number;
	upgradePack: number;
	upgradeVariantId: string;
	savings: number;
	currency: string;
	qualifiesForFreeShipping: boolean;
}

interface BestsellerOffer {
	type: "bestseller";
	productSlug: string;
	productName: string;
	thumbnailUrl: string;
	cheapestVariantId: string;
	cheapestPrice: number;
	currency: string;
	amountToFreeShipping: number;
}

type UpsellOffer = UpgradeOffer | BestsellerOffer;

/**
 * Parse pack size from variant name, e.g. "Retatrutide 10mg - 2 Pack" => 2
 */
function parsePackSize(variantName: string): number {
	const match = variantName.match(/(\d+)\s*(?:-\s*)?Pack/i);
	return match ? parseInt(match[1]!, 10) : 0;
}

/**
 * Find the best upgrade variant: look for the next pack size up.
 * Returns the upgrade variant and the savings amount.
 */
function findUpgradeVariant(
	currentVariantName: string,
	currentPrice: number,
	allVariants: UpsellVariantData[],
): { variant: UpsellVariantData; savings: number } | null {
	const currentPack = parsePackSize(currentVariantName);
	if (currentPack === 0 || currentPack >= 3) return null;

	const targetPack = currentPack + 1;

	// Find the variant with the target pack size
	const upgradeVariant = allVariants.find((v) => {
		const packSize = parsePackSize(v.name);
		return packSize === targetPack && (v.quantityAvailable == null || v.quantityAvailable > 0);
	});

	if (!upgradeVariant) return null;

	// Calculate savings: (currentPrice * targetPack / currentPack) - upgradePrice
	// i.e. what they'd pay buying individual packs vs the bundle
	const expectedPrice = (currentPrice / currentPack) * targetPack;
	const savings = expectedPrice - upgradeVariant.price;

	if (savings <= 0) return null;

	return { variant: upgradeVariant, savings };
}

export function CartUpsell({ lines, subtotal, checkoutId, channel, locale }: CartUpsellProps) {
	const t = useTranslations("cart");
	const loc = locale && isValidLocale(locale) ? (locale as Locale) : defaultLocale;
	const fmt = (amount: number, cur: string) => formatPrice(amount, cur, loc);
	const [isPending, startTransition] = useTransition();
	const gateAction = useGateCartAction();
	const [offer, setOffer] = useState<UpsellOffer | null>(null);
	const [isLoading, setIsLoading] = useState(true);
	const [resolvedMoney, setResolvedMoney] = useState<Map<string, ResolvedMoneyPresentation> | null>(null);

	const threshold =
		SHIPPING_CONFIG.freeShippingThreshold[channel as keyof typeof SHIPPING_CONFIG.freeShippingThreshold] ??
		SHIPPING_CONFIG.freeShippingThreshold.eu;

	useEffect(() => {
		let cancelled = false;

		async function resolveOffer() {
			setIsLoading(true);

			// Step 1: Try pack upgrade for items in cart
			for (const line of lines) {
				if (line.saleUnit?.toLowerCase() === "box") continue;
				const packSize = parsePackSize(line.variantName);
				if (packSize === 0 || packSize >= 3) continue;

				try {
					const productData = await fetchProductForUpsell(line.productSlug, channel, locale);
					if (!productData || cancelled) continue;

					const upgrade = findUpgradeVariant(line.variantName, line.totalPrice, productData.variants);
					if (upgrade) {
						const newSubtotal = subtotal - line.totalPrice + upgrade.variant.price;
						setOffer({
							type: "upgrade",
							lineId: line.id,
							productName: line.productName,
							thumbnailUrl: normalizeSaleorMediaUrl(productData.thumbnailUrl || line.thumbnailUrl),
							currentPack: packSize,
							upgradePack: packSize + 1,
							upgradeVariantId: upgrade.variant.id,
							savings: upgrade.savings,
							currency: upgrade.variant.currency,
							qualifiesForFreeShipping: newSubtotal >= threshold,
						});
						setIsLoading(false);
						return;
					}
				} catch {
					// Continue to next line or fallback
				}
			}

			if (cancelled) return;

			// Step 2: Bestseller fallback
			const cartSlugs = new Set(lines.map((l) => l.productSlug));

			for (const slug of UPSELL_BESTSELLERS) {
				if (cartSlugs.has(slug)) continue;

				try {
					const productData = await fetchProductForUpsell(slug, channel, locale);
					if (!productData || cancelled) continue;

					// Find cheapest available variant
					const available = productData.variants
						.filter((v) => v.quantityAvailable == null || v.quantityAvailable > 0)
						.sort((a, b) => a.price - b.price);

					if (available.length === 0) continue;

					const cheapest = available[0]!;
					setOffer({
						type: "bestseller",
						productSlug: productData.slug,
						productName: productData.name,
						thumbnailUrl: normalizeSaleorMediaUrl(productData.thumbnailUrl),
						cheapestVariantId: cheapest.id,
						cheapestPrice: cheapest.price,
						currency: cheapest.currency,
						amountToFreeShipping: Math.max(threshold - subtotal, 0),
					});
					setIsLoading(false);
					return;
				} catch {
					// Continue to next bestseller
				}
			}

			if (!cancelled) {
				setOffer(null);
				setIsLoading(false);
			}
		}

		resolveOffer();
		return () => {
			cancelled = true;
		};
	}, [lines, subtotal, channel, locale, threshold]);

	useEffect(() => {
		if (!offer) {
			queueMicrotask(() => setResolvedMoney(null));
			return;
		}

		let cancelled = false;
		const items: Array<{ id: string; amount: number; currency: string }> =
			offer.type === "upgrade"
				? [{ id: "savings", amount: offer.savings, currency: offer.currency }]
				: [
						{ id: "cheapest", amount: offer.cheapestPrice, currency: offer.currency },
						...(offer.amountToFreeShipping > 0
							? [{ id: "freeShip", amount: offer.amountToFreeShipping, currency: offer.currency }]
							: []),
					];

		(async () => {
			const rows = await fetchPricingResolveItems({ locale, channel, items });
			if (cancelled || !rows) return;
			setResolvedMoney(pricingItemsById(rows));
		})();

		return () => {
			cancelled = true;
		};
	}, [offer, locale, channel]);

	const handleUpgrade = (upgradeOffer: UpgradeOffer) => {
		gateAction(() => {
			startTransition(async () => {
				await upgradeCartLine(checkoutId, upgradeOffer.lineId, upgradeOffer.upgradeVariantId);
			});
		});
	};

	const handleAddBestseller = (bestsellerOffer: BestsellerOffer) => {
		gateAction(() => {
			startTransition(async () => {
				await addCartLine(checkoutId, bestsellerOffer.cheapestVariantId);
			});
		});
	};

	// Don't render anything while loading or if no offer available
	if (isLoading || !offer) return null;

	return (
		<div className="px-5 py-2.5">
			<div className="bg-secondary/30 flex items-center gap-3 rounded-lg border border-border p-2.5">
				{/* Thumbnail */}
				{offer.thumbnailUrl && (
					<div className="relative h-12 w-10 shrink-0 overflow-hidden rounded bg-secondary">
						<Image
							src={offer.thumbnailUrl}
							alt={offer.productName}
							fill
							className="object-cover"
							sizes="40px"
						/>
					</div>
				)}

				{/* Content */}
				<div className="min-w-0 flex-1">
					<p className="truncate text-sm font-medium">{offer.productName}</p>

					{offer.type === "upgrade" ? (
						<>
							<p className="mt-0.5 text-xs text-muted-foreground">
								<TrendingUp className="mr-1 inline-block h-3 w-3" />
								{t("upgradeAndSave", {
									count: offer.upgradePack,
									amount: resolvedMoney?.get("savings")?.primaryLabel ?? fmt(offer.savings, offer.currency),
								})}
							</p>
							{offer.qualifiesForFreeShipping && (
								<p className="mt-0.5 text-xs font-medium text-success">{t("plusFreeShipping")}</p>
							)}
						</>
					) : (
						<>
							<p className="mt-0.5 text-xs text-muted-foreground">
								<Sparkles className="mr-1 inline-block h-3 w-3" />
								{t("bestseller")} &ndash;{" "}
								{t("startingFrom", {
									price:
										resolvedMoney?.get("cheapest")?.primaryLabel ?? fmt(offer.cheapestPrice, offer.currency),
								})}
							</p>
							{offer.amountToFreeShipping > 0 && (
								<p className="mt-0.5 text-xs text-muted-foreground">
									{t("amountToFreeShipping", {
										amount:
											resolvedMoney?.get("freeShip")?.primaryLabel ??
											fmt(offer.amountToFreeShipping, offer.currency),
									})}
								</p>
							)}
						</>
					)}
				</div>

				{/* CTA Button */}
				<button
					type="button"
					disabled={isPending}
					onClick={() => {
						if (offer.type === "upgrade") {
							handleUpgrade(offer);
						} else {
							handleAddBestseller(offer);
						}
					}}
					className={cn(
						"inline-flex shrink-0 items-center gap-1 rounded-md bg-foreground px-3 py-1.5 text-xs font-medium text-background transition-opacity hover:opacity-90",
						isPending && "cursor-not-allowed opacity-50",
					)}
				>
					{offer.type === "upgrade" ? t("upgrade") : t("addToCart")}
					<ArrowRight className="h-3 w-3" />
				</button>
			</div>
		</div>
	);
}
